import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid, Chip,
  IconButton, Tooltip, Alert, CircularProgress, MenuItem,
  InputAdornment, FormControlLabel, Switch
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import RefreshIcon from '@mui/icons-material/Refresh';
import * as XLSX from 'xlsx';

const TIPO_CUENTAS = ['ACTIVO','PASIVO','PATRIMONIO','INGRESO','GASTO','COSTO','ORDEN'];
const NATURALEZA = [{ v:'D', l:'Deudora' }, { v:'A', l:'Acreedora' }];

const EMPTY_CUENTA = {
  codigo: '', descripcion: '', descripcion_corta: '', nivel: 6, naturaleza: 'D',
  tipo_cuenta: 'ACTIVO', es_hoja: true, requiere_auxiliar: false,
  auxiliar_tipo: '', requiere_cencosto: false,
  afecta_balance: true, afecta_resultados: false, saldo_normal: 'D',
  acepta_debe: true, acepta_haber: true, cuenta_destino_id: null, urc_codigo_default: ''
};

const URC_OPCIONES = [
  { codigo: '', label: 'Sin URC (no es cuenta de efectivo)' },
  { codigo: '10', label: '10 — Cobros a clientes (ventas)', tipo: 'OPE' },
  { codigo: '11', label: '11 — Cobros por honorarios/regalías', tipo: 'OPE' },
  { codigo: '12', label: '12 — Cobros de intereses y dividendos', tipo: 'OPE' },
  { codigo: '13', label: '13 — Otros cobros de operación', tipo: 'OPE' },
  { codigo: '14', label: '14 — Pagos a proveedores', tipo: 'OPE' },
  { codigo: '15', label: '15 — Pagos al personal y BBSS', tipo: 'OPE' },
  { codigo: '16', label: '16 — Pagos de tributos e impuestos', tipo: 'OPE' },
  { codigo: '17', label: '17 — Pagos de intereses (operación)', tipo: 'OPE' },
  { codigo: '18', label: '18 — Otros pagos de operación', tipo: 'OPE' },
  { codigo: '20', label: '20 — Cobros por venta de valores/inversiones', tipo: 'INV' },
  { codigo: '21', label: '21 — Cobros por venta de inmuebles/maq/eq', tipo: 'INV' },
  { codigo: '22', label: '22 — Cobros por venta de intangibles', tipo: 'INV' },
  { codigo: '23', label: '23 — Cobros por recuperación de préstamos', tipo: 'INV' },
  { codigo: '24', label: '24 — Otros cobros de inversión', tipo: 'INV' },
  { codigo: '25', label: '25 — Pagos por compra de inversiones', tipo: 'INV' },
  { codigo: '26', label: '26 — Pagos por compra de inmuebles/maq/eq', tipo: 'INV' },
  { codigo: '27', label: '27 — Pagos por compra de intangibles', tipo: 'INV' },
  { codigo: '28', label: '28 — Otros pagos de inversión', tipo: 'INV' },
  { codigo: '30', label: '30 — Cobros por emisión de acciones/aportes', tipo: 'FIN' },
  { codigo: '31', label: '31 — Cobros de préstamos / deuda LP', tipo: 'FIN' },
  { codigo: '32', label: '32 — Otros cobros de financiamiento', tipo: 'FIN' },
  { codigo: '33', label: '33 — Pagos de amortización de préstamos', tipo: 'FIN' },
  { codigo: '34', label: '34 — Pagos de dividendos/utilidades', tipo: 'FIN' },
  { codigo: '35', label: '35 — Otros pagos de financiamiento', tipo: 'FIN' },
];

// Cuenta necesita destino si es hoja, empieza con 6 o 9, y NO empieza con 66
function necesitaDestino(codigo, esHoja) {
  if (!esHoja) return false;
  const c = String(codigo || '');
  return (c.startsWith('6') || c.startsWith('9')) && !c.startsWith('66');
}

const NIVEL_LABELS = { 1:'Elemento', 2:'Cuenta', 3:'Subcuenta', 4:'Divisionaria', 5:'Sub-divisionaria', 6:'Analítica' };

function colorNivel(nivel) {
  const cols = {
    1: '#1565c0',  // Elemento — azul oscuro
    2: '#0277bd',  // Cuenta — azul
    3: '#0288d1',  // Subcuenta — azul claro
    4: '#0097a7',  // Divisionaria — cyan
    5: '#00796b',  // Sub-divisionaria — teal
    6: '#388e3c',  // Analítica — verde (cuenta de imputación)
  };
  return cols[nivel] || '#546e7a';
}
function textNivel() {
  return 'white';
}

export default function PlanContable() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buscar, setBuscar] = useState('');
  const [soloHojas, setSoloHojas] = useState(false);
  const [open, setOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(EMPTY_CUENTA);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [importLog, setImportLog] = useState(null);
  const [cuentasDestino, setCuentasDestino] = useState([]);
  const [destInputVal, setDestInputVal] = useState('');
  const [periodos, setPeriodos] = useState([]);
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenDesde, setRegenDesde] = useState('');
  const [regenHasta, setRegenHasta] = useState('');
  const [regenPreview, setRegenPreview] = useState(null);
  const [regenLoading, setRegenLoading] = useState(false);
  const [regenError, setRegenError] = useState('');
  const [regenAplicado, setRegenAplicado] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    axios.get('/api/contabilidad/empresas')
      .then(r => {
        const todas = Array.isArray(r.data) ? r.data : [];
        setEmpresas(todas);
        const activas = todas.filter(e => e.activo);
        if (activas.length === 1) setEmpresaId(String(activas[0].id));
      })
      .catch(() => {});
  }, []);

  const cargar = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    try {
      const params = { empresa_id: empresaId };
      if (soloHojas) params.solo_hojas = '1';
      if (buscar) params.buscar = buscar;
      const r = await axios.get('/api/contabilidad/plan', { params });
      setCuentas(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError('Error al cargar plan de cuentas');
    } finally {
      setLoading(false);
    }
  }, [empresaId, soloHojas, buscar]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    if (!empresaId) { setPeriodos([]); return; }
    axios.get('/api/contabilidad/periodos', { params: { empresa_id: empresaId } })
      .then(r => setPeriodos(Array.isArray(r.data) ? r.data : []))
      .catch(() => setPeriodos([]));
  }, [empresaId]);

  const empresa = empresas.find(e => String(e.id) === String(empresaId));

  const abrirRegenerar = () => {
    setRegenOpen(true); setRegenPreview(null); setRegenAplicado(null); setRegenError('');
    setRegenDesde(''); setRegenHasta('');
  };

  const previsualizarRegenerar = async () => {
    if (!regenDesde || !regenHasta) { setRegenError('Seleccione período desde y hasta'); return; }
    setRegenLoading(true); setRegenError(''); setRegenPreview(null); setRegenAplicado(null);
    try {
      const r = await axios.post('/api/contabilidad/regenerar-destinos', {
        empresa_id: empresaId, periodo_desde: regenDesde, periodo_hasta: regenHasta, dry_run: true
      });
      setRegenPreview(r.data);
    } catch (e) { setRegenError(e.response?.data?.error || e.message); }
    setRegenLoading(false);
  };

  const aplicarRegenerar = async () => {
    setRegenLoading(true); setRegenError('');
    try {
      const r = await axios.post('/api/contabilidad/regenerar-destinos', {
        empresa_id: empresaId, periodo_desde: regenDesde, periodo_hasta: regenHasta, dry_run: false
      });
      setRegenAplicado(r.data);
      setRegenPreview(null);
    } catch (e) { setRegenError(e.response?.data?.error || e.message); }
    setRegenLoading(false);
  };

  // Cargar opciones para el autocomplete de cuenta destino (cuentas hoja clase 6 y 9)
  const buscarCuentasDestino = useCallback(async (termino) => {
    if (!empresaId) return;
    try {
      const r = await axios.get('/api/contabilidad/plan', {
        params: { empresa_id: empresaId, solo_hojas: '1', buscar: termino || '' }
      });
      setCuentasDestino(Array.isArray(r.data) ? r.data.slice(0, 50) : []);
    } catch (_) {}
  }, [empresaId]);

  const abrirNueva = () => {
    setEditando(null);
    setForm({ ...EMPTY_CUENTA });
    setError('');
    setOpen(true);
  };

  const abrirEditar = (c) => {
    setEditando(c);
    setForm({
      codigo: c.codigo,
      descripcion: c.descripcion,
      descripcion_corta: c.descripcion_corta || '',
      nivel: c.nivel,
      naturaleza: c.naturaleza,
      tipo_cuenta: c.tipo_cuenta,
      es_hoja: !!c.es_hoja,
      requiere_auxiliar: !!c.requiere_auxiliar,
      auxiliar_tipo: c.auxiliar_tipo || '',
      requiere_cencosto: !!c.requiere_cencosto,
      afecta_balance: !!c.afecta_balance,
      afecta_resultados: !!c.afecta_resultados,
      saldo_normal: c.saldo_normal || 'D',
      acepta_debe: !!c.acepta_debe,
      acepta_haber: !!c.acepta_haber,
      activo: c.activo !== 0,
      cuenta_destino_id: c.cuenta_destino_id || null,
      urc_codigo_default: c.urc_codigo_default || ''
    });
    setDestInputVal(c.cuenta_destino_codigo ? `${c.cuenta_destino_codigo} - ${c.cuenta_destino_descripcion}` : '');
    if (necesitaDestino(c.codigo, c.es_hoja)) buscarCuentasDestino('');
    setError('');
    setOpen(true);
  };

  const guardar = async () => {
    if (!form.codigo || !form.descripcion) {
      setError('Código y descripción son requeridos');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const body = {
        ...form,
        empresa_id: empresaId,
        es_hoja: form.es_hoja ? 1 : 0,
        requiere_auxiliar: form.requiere_auxiliar ? 1 : 0,
        requiere_cencosto: form.requiere_cencosto ? 1 : 0,
        afecta_balance: form.afecta_balance ? 1 : 0,
        afecta_resultados: form.afecta_resultados ? 1 : 0,
        acepta_debe: form.acepta_debe ? 1 : 0,
        acepta_haber: form.acepta_haber ? 1 : 0,
        activo: form.activo !== false ? 1 : 0
      };
      if (editando) {
        await axios.put(`/api/contabilidad/plan/${editando.id}`, body);
      } else {
        await axios.post('/api/contabilidad/plan', body);
      }
      setSuccess(editando ? 'Cuenta actualizada' : 'Cuenta creada');
      setOpen(false);
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Desactivar esta cuenta?')) return;
    try {
      await axios.delete(`/api/contabilidad/plan/${id}`);
      setSuccess('Cuenta desactivada');
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const handleFileImport = async (e) => {
    const file = e.target.files[0];
    if (!file || !empresaId) return;
    e.target.value = '';

    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });

      const cuentasImport = rows.map(r => {
        const codigo = String(r.CODIGO || r.Codigo || r.codigo || '').trim();
        const descripcion = String(r.DESCRIPCION || r.Descripcion || r.descripcion || '').trim();
        const nivel = parseInt(r.NIVEL || r.Nivel || r.nivel || codigo.length) || 6;
        const naturaleza = String(r.NATURALEZA || r.Naturaleza || r.naturaleza || 'D').trim().toUpperCase();
        const tipo_cuenta = String(r.TIPO_CUENTA || r.TipoCuenta || r.tipo_cuenta || 'ACTIVO').trim().toUpperCase();
        const es_hoja = r.ES_HOJA !== undefined
          ? (r.ES_HOJA === 1 || r.ES_HOJA === '1' || r.ES_HOJA === 'SI' || r.ES_HOJA === true)
          : nivel >= 6;
        return { codigo, descripcion, nivel, naturaleza, tipo_cuenta, es_hoja };
      }).filter(r => r.codigo && r.descripcion);

      if (cuentasImport.length === 0) {
        setError('No se encontraron filas válidas. Columnas requeridas: CODIGO, DESCRIPCION');
        return;
      }

      setLoading(true);
      const r = await axios.post('/api/contabilidad/plan/importar', {
        empresa_id: empresaId, cuentas: cuentasImport, reemplazar: false
      });
      setImportLog(r.data);
      setSuccess(`Importación completada: ${r.data.nuevas} cuentas procesadas`);
      cargar();
    } catch (e) {
      setError('Error al importar: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          <AccountTreeIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h5" fontWeight="bold">Plan de Cuentas</Typography>
        </Box>
        <TextField select label="Empresa" size="small" value={empresaId}
          onChange={e => setEmpresaId(e.target.value)}
          sx={{ minWidth: { xs: '100%', sm: 260 }, ml: { sm: 'auto' } }}>
          {empresas.map(e => (
            <MenuItem key={e.id} value={String(e.id)}>
              {e.razon_social}{!e.activo && <span style={{ color: '#999', marginLeft: 6 }}>(Inactiva)</span>}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && !open && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {importLog && (
        <Alert severity={importLog.errores > 0 ? 'warning' : 'success'} sx={{ mb: 2 }} onClose={() => setImportLog(null)}>
          Importación: {importLog.nuevas} cuentas OK
          {importLog.errores > 0 && ` · ${importLog.errores} errores`}
          {importLog.detalle_errores?.length > 0 && (
            <Box component="pre" sx={{ fontSize: 11, mt: 1, maxHeight: 100, overflow: 'auto' }}>
              {importLog.detalle_errores.join('\n')}
            </Box>
          )}
        </Alert>
      )}

      {!empresaId ? (
        <Alert severity="info">Seleccione una empresa para ver su plan de cuentas.</Alert>
      ) : (
        <>
          <Grid container spacing={1.5} sx={{ mb: 2 }} alignItems="center">
            <Grid item xs={12} sm="auto" sx={{ flexGrow: 1 }}>
              <TextField fullWidth size="small" placeholder="Buscar código o descripción..."
                value={buscar} onChange={e => setBuscar(e.target.value)}
                sx={{ minWidth: { xs: '100%', sm: 240 }, maxWidth: { sm: 340 } }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm="auto">
              <FormControlLabel sx={{ m: 0 }}
                control={<Switch size="small" checked={soloHojas} onChange={e => setSoloHojas(e.target.checked)} />}
                label={<Typography variant="body2">Solo analíticas</Typography>}
              />
            </Grid>
            <Grid item xs="auto" sm="auto" sx={{ ml: { sm: 'auto' } }}>
              <Tooltip title="Importar desde Excel (columnas: CODIGO, DESCRIPCION, NIVEL, NATURALEZA, TIPO_CUENTA)">
                <Button variant="outlined" startIcon={<UploadFileIcon />}
                  onClick={() => fileRef.current?.click()} disabled={loading} size="small">
                  Importar
                </Button>
              </Tooltip>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
                onChange={handleFileImport} />
            </Grid>
            <Grid item xs="auto">
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={cargar} disabled={loading} size="small">
                Actualizar
              </Button>
            </Grid>
            <Grid item xs="auto">
              <Tooltip title="Vuelve a generar las líneas destino (clase 6→9) de los asientos de Registro de Compras/Ventas ya grabados, usando la configuración actual de Cuenta Destino">
                <Button variant="outlined" color="secondary" startIcon={<AccountTreeIcon />}
                  onClick={abrirRegenerar} disabled={!empresaId} size="small">
                  Regenerar Destinos
                </Button>
              </Tooltip>
            </Grid>
            <Grid item xs="auto">
              <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNueva} disabled={!empresaId} size="small">
                Nueva Cuenta
              </Button>
            </Grid>
          </Grid>

          {empresa && (
            <Box sx={{ mb: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label={`${cuentas.length} cuentas`} size="small" />
              <Chip label={`Mín. ${empresa.longitud_cuenta_min} dígitos`} size="small" color="info" variant="outlined" />
              <Chip label={`Máx. ${empresa.longitud_cuenta_max} dígitos`} size="small" color="info" variant="outlined" />
            </Box>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={2} sx={{ maxHeight: 600, overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {['Código', 'Descripción', 'Nivel', 'Naturaleza', 'Tipo', 'Hoja', 'Saldo normal', 'Acciones'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cuentas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        {buscar ? 'No se encontraron cuentas con ese criterio.' : 'No hay cuentas en este plan.'}
                      </TableCell>
                    </TableRow>
                  ) : cuentas.map(c => (
                    <TableRow key={c.id} hover sx={{ opacity: c.activo ? 1 : 0.5 }}>
                      <TableCell sx={{ pl: Math.max(1, c.nivel - 1) * 1.5 }}>
                        <Typography variant="body2" fontWeight={c.nivel <= 2 ? 'bold' : c.nivel <= 4 ? 'medium' : 'normal'}
                          fontFamily="monospace" sx={{ color: colorNivel(c.nivel) }}>
                          {c.codigo}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ pl: Math.max(1, c.nivel - 1) * 1.5 }}>
                        <Typography variant="body2"
                          fontWeight={c.nivel <= 2 ? 'bold' : c.nivel <= 4 ? '500' : 'normal'}
                          color={c.nivel <= 4 ? 'text.primary' : colorNivel(c.nivel)}>
                          {c.descripcion}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={NIVEL_LABELS[c.nivel] || `N${c.nivel}`} size="small"
                          sx={{ bgcolor: colorNivel(c.nivel), color: textNivel(c.nivel), fontWeight: 'bold', fontSize: 10 }} />
                      </TableCell>
                      <TableCell>
                        <Chip label={c.naturaleza === 'D' ? 'Deudora' : 'Acreedora'} size="small"
                          color={c.naturaleza === 'D' ? 'info' : 'warning'} variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{c.tipo_cuenta}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        {c.es_hoja ? <Chip label="Sí" size="small" color="success" /> : <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell>
                        <Chip label={c.saldo_normal === 'D' ? 'Deudor' : 'Acreedor'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => abrirEditar(c)}><EditIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Desactivar">
                          <IconButton size="small" color="error" onClick={() => eliminar(c.id)}><DeleteIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editando ? `Editar cuenta ${editando.codigo}` : 'Nueva Cuenta Contable'}</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={4}>
              <TextField label="Código" name="codigo" value={form.codigo} onChange={handleChange}
                fullWidth size="small" required disabled={!!editando}
                inputProps={{ maxLength: 12 }}
                helperText={`${form.codigo.length} dígitos`} />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField label="Descripción" name="descripcion" value={form.descripcion}
                onChange={handleChange} fullWidth size="small" required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Descripción corta (opcional)" name="descripcion_corta"
                value={form.descripcion_corta} onChange={handleChange} fullWidth size="small"
                inputProps={{ maxLength: 80 }} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField label="Nivel" name="nivel" type="number" value={form.nivel}
                onChange={handleChange} fullWidth size="small"
                inputProps={{ min: 1, max: 10 }}
                helperText={NIVEL_LABELS[form.nivel] || 'Personalizado'} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField label="Saldo normal" name="saldo_normal" select value={form.saldo_normal}
                onChange={handleChange} fullWidth size="small">
                <MenuItem value="D">Deudor</MenuItem>
                <MenuItem value="A">Acreedor</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Naturaleza" name="naturaleza" select value={form.naturaleza}
                onChange={handleChange} fullWidth size="small">
                {NATURALEZA.map(n => <MenuItem key={n.v} value={n.v}>{n.l}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Tipo de cuenta" name="tipo_cuenta" select value={form.tipo_cuenta}
                onChange={handleChange} fullWidth size="small">
                {TIPO_CUENTAS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel control={
                <Switch name="es_hoja" checked={form.es_hoja} onChange={handleChange} />
              } label="Cuenta analítica (hoja)" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel control={
                <Switch name="afecta_balance" checked={form.afecta_balance} onChange={handleChange} />
              } label="Afecta balance" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel control={
                <Switch name="afecta_resultados" checked={form.afecta_resultados} onChange={handleChange} />
              } label="Afecta resultados" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel control={
                <Switch name="requiere_auxiliar" checked={form.requiere_auxiliar} onChange={handleChange} />
              } label="Requiere auxiliar" />
            </Grid>
            {form.requiere_auxiliar && (
              <Grid item xs={12} sm={6}>
                <TextField label="Tipo auxiliar" name="auxiliar_tipo" select value={form.auxiliar_tipo}
                  onChange={handleChange} fullWidth size="small" sx={{ minWidth: 170 }}>
                  {['CLIENTE','PROVEEDOR','EMPLEADO','BANCO','OTRO'].map(t =>
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  )}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12} sm={4}>
              <FormControlLabel control={
                <Switch name="requiere_cencosto" checked={form.requiere_cencosto} onChange={handleChange} />
              } label="Usa centro de costo" />
            </Grid>
            {editando && (
              <Grid item xs={12} sm={4}>
                <FormControlLabel control={
                  <Switch name="activo" checked={form.activo !== false} onChange={handleChange} />
                } label="Activo" />
              </Grid>
            )}
            {form.es_hoja && (
              <Grid item xs={12} sm={6}>
                <TextField label="URC por defecto (Flujo de Efectivo)" name="urc_codigo_default"
                  select value={form.urc_codigo_default || ''} size="small" fullWidth
                  SelectProps={{ displayEmpty: true }}
                  InputLabelProps={{ shrink: true }}
                  onChange={handleChange}
                  helperText="Se auto-sugiere al seleccionar esta cuenta en un asiento">
                  {URC_OPCIONES.map(u => (
                    <MenuItem key={u.codigo || '__none__'} value={u.codigo} sx={{ fontSize: 12 }}>
                      {u.tipo && <span style={{ fontFamily: 'monospace', fontWeight: 'bold', marginRight: 8,
                        color: u.tipo === 'OPE' ? '#1565c0' : u.tipo === 'INV' ? '#2e7d32' : '#6a1b9a' }}>{u.codigo}</span>}
                      {u.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            {necesitaDestino(form.codigo, form.es_hoja) && (
              <Grid item xs={12}>
                <Autocomplete
                  options={cuentasDestino}
                  getOptionLabel={o => `${o.codigo} - ${o.descripcion}`}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={cuentasDestino.find(c => c.id === form.cuenta_destino_id) || null}
                  inputValue={destInputVal}
                  onInputChange={(_, val) => {
                    setDestInputVal(val);
                    buscarCuentasDestino(val);
                  }}
                  onChange={(_, val) => setForm(f => ({ ...f, cuenta_destino_id: val?.id || null }))}
                  renderInput={params => (
                    <TextField {...params} label="Cuenta Destino (clase 9 o 6 según plan costos)" size="small"
                      helperText="Cuenta contabilizadora de costos/gastos vinculada a esta cuenta analítica" />
                  )}
                  renderOption={(props, o) => (
                    <li {...props} key={o.id}>
                      <span style={{ fontFamily: 'monospace', color: '#388e3c', marginRight: 8 }}>{o.codigo}</span>
                      {o.descripcion}
                    </li>
                  )}
                  noOptionsText="Sin resultados"
                  size="small"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={guardar} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : editando ? 'Guardar' : 'Crear cuenta'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={regenOpen} onClose={() => setRegenOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'secondary.main', color: 'white', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountTreeIcon /> Regenerar Destinos (clase 6→9)
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#fafbfc' }}>
          <Alert severity="info" variant="outlined" icon={<AccountTreeIcon fontSize="small" />} sx={{ mb: 2.5 }}>
            Borra y vuelve a generar <strong>solo</strong> las líneas destino automáticas
            (clase 6→9 + cuenta 79) de los asientos de Registro de Compras/Ventas del rango
            elegido, usando la configuración <strong>actual</strong> de "Cuenta Destino" en Plan
            de Cuentas. No toca ninguna otra línea del asiento (CxC/CxP, auxiliar, IGV, etc.).
          </Alert>

          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Rango de período</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
            <TextField select label="Período desde" size="small" value={regenDesde}
              sx={{ width: '14em' }}
              onChange={e => { setRegenDesde(e.target.value); setRegenPreview(null); setRegenAplicado(null); }}>
              {periodos.map(p => <MenuItem key={p.periodo} value={p.periodo}>{p.periodo}</MenuItem>)}
            </TextField>
            <TextField select label="Período hasta" size="small" value={regenHasta}
              sx={{ width: '14em' }}
              onChange={e => { setRegenHasta(e.target.value); setRegenPreview(null); setRegenAplicado(null); }}>
              {periodos.map(p => <MenuItem key={p.periodo} value={p.periodo}>{p.periodo}</MenuItem>)}
            </TextField>
          </Box>

          {regenError && <Alert severity="error" sx={{ mb: 2 }}>{regenError}</Alert>}

          {regenPreview && (
            <Paper variant="outlined" sx={{
              p: 2, mb: 1,
              bgcolor: regenPreview.asientos_modificados > 0 ? '#fff8e1' : '#e8f5e9',
              borderColor: regenPreview.asientos_modificados > 0 ? '#f9a825' : '#2e7d32',
              borderWidth: 2,
            }}>
              <Typography fontWeight="bold" color={regenPreview.asientos_modificados > 0 ? '#e65100' : '#2e7d32'}>
                {regenPreview.asientos_modificados === 0
                  ? `Sin cambios — los ${regenPreview.asientos_revisados} asiento(s) revisados ya coinciden con la configuración actual.`
                  : `${regenPreview.asientos_modificados} de ${regenPreview.asientos_revisados} asiento(s) cambiarían`}
              </Typography>
              {regenPreview.asientos_modificados > 0 && (
                <Typography variant="body2" sx={{ mt: 0.5, fontFamily: 'monospace' }}>
                  Monto destino actual: S/ {regenPreview.monto_destino_anterior.toFixed(2)} → nuevo: S/ {regenPreview.monto_destino_nuevo.toFixed(2)}
                </Typography>
              )}
            </Paper>
          )}

          {regenAplicado && (
            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#e8f5e9', borderColor: '#2e7d32', borderWidth: 2 }}>
              <Typography fontWeight="bold" color="#2e7d32">
                Aplicado: {regenAplicado.asientos_modificados} asiento(s) regenerados de {regenAplicado.asientos_revisados} revisados.
              </Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRegenOpen(false)} disabled={regenLoading}>Cerrar</Button>
          <Button onClick={previsualizarRegenerar} variant="outlined" color="secondary" disabled={regenLoading || !regenDesde || !regenHasta}>
            {regenLoading && !regenPreview ? <CircularProgress size={20} /> : 'Vista previa'}
          </Button>
          <Button onClick={aplicarRegenerar} variant="contained" color="warning"
            disabled={regenLoading || !regenPreview || regenPreview.asientos_modificados === 0}>
            {regenLoading && regenPreview ? <CircularProgress size={20} /> : 'Aplicar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
